import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Reveal, Eyebrow } from "./Reveal";
import { IMAGES, EFFECTIF_OPTIONS, ILE_OPTIONS, PERIODE_OPTIONS } from "../../data";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { CheckCircle2, ShieldCheck, Clock, UserCheck, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GUARANTEES = [
  { icon: Clock, text: "Réponse sous 24h ouvrées" },
  { icon: ShieldCheck, text: "Devis 100% gratuit sans engagement" },
  { icon: UserCheck, text: "Accompagnateur CSE dédié" },
];

const empty = {
  nom_complet: "", email_pro: "", telephone: "", nom_entreprise: "",
  effectif: "", ile_souhaitee: "", periode_souhaitee: "", commentaires: "",
};

export const LeadForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const c = e.detail?.count ?? 0;
      const opt = c <= 25 ? EFFECTIF_OPTIONS[0] : c <= 50 ? EFFECTIF_OPTIONS[1] : c <= 100 ? EFFECTIF_OPTIONS[2] : EFFECTIF_OPTIONS[3];
      setForm((f) => ({ ...f, effectif: opt }));
    };
    window.addEventListener("prefill-effectif", handler);
    return () => window.removeEventListener("prefill-effectif", handler);
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const required = ["nom_complet", "email_pro", "telephone", "nom_entreprise", "effectif", "ile_souhaitee", "periode_souhaitee"];
    for (const k of required) {
      if (!form[k]) { toast.error("Merci de compléter tous les champs obligatoires."); return; }
    }
    setLoading(true);
    try {
      await axios.post(`${API}/propositions`, form);
      setDone(true);
      toast.success("Demande envoyée ! Notre coordinateur vous recontacte sous 24h.");
    } catch (err) {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="formulaire-devis" className="relative bg-ocean-dark py-24 sm:py-32 overflow-hidden" data-testid="lead-form-section">
      <div className="absolute inset-0 opacity-25">
        <img src={IMAGES.formBg} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-ocean-dark via-ocean-dark/92 to-ocean-dark" />
      <div className="absolute -top-20 right-10 w-96 h-96 rounded-full bg-coral/20 blur-[120px] pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6 sm:px-8">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-14 items-start">
          <div className="lg:pt-6">
            <Reveal><Eyebrow>Réservez votre date privilège</Eyebrow></Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-serif font-semibold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.08]">
                Demandez votre proposition personnalisée en moins de 24h
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 text-base text-white/70 leading-relaxed">
                Remplissez ce formulaire confidentiel. Notre coordinateur bien-être dédié aux CSE
                vous contactera avec un devis détaillé et les disponibilités des hôtels.
              </p>
            </Reveal>
            <div className="mt-8 space-y-3">
              {GUARANTEES.map((g, i) => (
                <Reveal key={i} delay={0.15 + i * 0.06}>
                  <div className="flex items-center gap-3 text-white/85 text-sm">
                    <span className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
                      <g.icon className="w-4 h-4 text-gold" />
                    </span>
                    {g.text}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.1}>
            <div className="rounded-[2rem] bg-sand-light border border-white/10 shadow-2xl p-6 sm:p-8">
              {done ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-10"
                  data-testid="form-success-message"
                >
                  <CheckCircle2 className="w-16 h-16 text-palm mx-auto" strokeWidth={1.4} />
                  <h3 className="mt-5 font-serif text-3xl font-semibold text-ocean">Merci {form.nom_complet.split(" ")[0]} !</h3>
                  <p className="mt-3 text-muted2 text-sm max-w-sm mx-auto">
                    Votre demande a bien été enregistrée. Notre coordinateur CSE vous recontacte
                    sous 24h ouvrées avec une proposition sur-mesure.
                  </p>
                  <button
                    onClick={() => { setForm(empty); setDone(false); }}
                    className="mt-6 text-sm font-semibold text-coral hover:text-coral-hover"
                    data-testid="form-reset-button"
                  >
                    Envoyer une autre demande
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Nom & Prénom *">
                      <Input data-testid="form-input-nom" value={form.nom_complet} onChange={(e) => set("nom_complet")(e.target.value)} placeholder="Catherine Marie-Joseph" className="bg-white" />
                    </Field>
                    <Field label="Email professionnel *">
                      <Input data-testid="form-input-email" type="email" value={form.email_pro} onChange={(e) => set("email_pro")(e.target.value)} placeholder="catherine@cse-entreprise.gp" className="bg-white" />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Téléphone *">
                      <Input data-testid="form-input-telephone" value={form.telephone} onChange={(e) => set("telephone")(e.target.value)} placeholder="0690 12 34 56" className="bg-white" />
                    </Field>
                    <Field label="Entreprise / CSE *">
                      <Input data-testid="form-input-entreprise" value={form.nom_entreprise} onChange={(e) => set("nom_entreprise")(e.target.value)} placeholder="CSE Air Caraïbes" className="bg-white" />
                    </Field>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Effectif *">
                      <SelectField testid="form-select-effectif" value={form.effectif} onChange={set("effectif")} placeholder="Participants" options={EFFECTIF_OPTIONS} />
                    </Field>
                    <Field label="Île souhaitée *">
                      <SelectField testid="form-select-ile" value={form.ile_souhaitee} onChange={set("ile_souhaitee")} placeholder="Destination" options={ILE_OPTIONS} />
                    </Field>
                  </div>
                  <Field label="Période envisagée *">
                    <SelectField testid="form-select-periode" value={form.periode_souhaitee} onChange={set("periode_souhaitee")} placeholder="Quand ?" options={PERIODE_OPTIONS} />
                  </Field>
                  <Field label="Précisions (optionnel)">
                    <Textarea data-testid="form-input-commentaires" value={form.commentaires} onChange={(e) => set("commentaires")(e.target.value)} placeholder="Besoins alimentaires, transport, objectifs prioritaires..." className="bg-white min-h-[84px]" />
                  </Field>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-coral hover:bg-coral-hover disabled:opacity-70 text-white font-semibold px-6 py-4 text-base transition-all hover:shadow-[0_14px_36px_rgba(230,92,0,0.4)] hover:-translate-y-0.5"
                    data-testid="form-submit-button"
                  >
                    {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Envoi en cours...</> : "Demander ma proposition"}
                  </button>
                  <p className="text-center text-xs text-muted2">Données confidentielles — réservé aux CSE, amicales & directions RH.</p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-ocean text-[13px] font-medium">{label}</Label>
    {children}
  </div>
);

const SelectField = ({ testid, value, onChange, placeholder, options }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="bg-white">
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o} value={o}>{o}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);
